import { CircleSpinner } from "common/spinners";
import { SectionBox } from "common/styleguide/SectionBox";
import { UnderlineSelector } from "common/UnderlineSelector";
import { Map } from "immutable";
import fetchJSON from "lib/fetchJSON";
import _ from "lodash";
import moment from "moment";
import React from "react";
import { AdminLayout, Grid } from "../layoutComponents";
import styles from "./DeviceAnalytics.scss";
const ChartJS = React.lazy(() => import("./chart"));

export class DeviceAnalytics extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      thirtyDayTotals: null,
      pastWeek: null,
      pastMonth: null,
      pastSixMonths: null,
      chartScope: window.localStorage.getItem("deviceAnalyticsChartScope") || "pastMonth",
    };
  }

  componentDidMount() {
    // fetch chart data
    this.fetchData();
    // always fetch thirty day totals
    fetchJSON("/admin/device_analytics.json?top_n=15&bucket_days=30&num_buckets=1").then((buckets) =>
      this.setState({ thirtyDayTotals: buckets[0] })
    );
  }

  componentDidUpdate() {
    this.fetchData();
  }

  setChartScope = (chartScope) => {
    this.setState({ chartScope });
    window.localStorage.setItem("deviceAnalyticsChartScope", chartScope);
  };

  // need to handle pending fetches!
  fetchData() {
    const { chartScope } = this.state;
    // there's a HUGE difference betwen topN = 9 and topN = 8 that doesn't
    // make sense. My guess is that it's related to the device_code `nil`
    const topN = 10;
    // changing topN should not change the shape of the graph at all, or the
    // total number of rides uploaded. wtf.
    if (chartScope === "pastWeek" && !this.state.pastWeek) {
      this.setState({ pastWeek: "pending" });
      fetchJSON(`/admin/device_analytics.json?top_n=${topN}&bucket_days=1&num_buckets=7`).then((pastWeek) =>
        this.setState({ pastWeek })
      );
    }
    if (chartScope === "pastMonth" && !this.state.pastMonth) {
      this.setState({ pastMonth: "pending" });
      fetchJSON(`/admin/device_analytics.json?top_n=${topN}&bucket_days=1&num_buckets=30`).then((pastMonth) =>
        this.setState({ pastMonth })
      );
    }
    if (chartScope === "pastSixMonths" && !this.state.pastSixMonths) {
      this.setState({ pastSixMonths: "pending" });
      fetchJSON(`/admin/device_analytics.json?top_n=${topN}&bucket_days=7&num_buckets=24`).then((pastSixMonths) =>
        this.setState({ pastSixMonths })
      );
    }
  }

  render() {
    const { chartScope, thirtyDayTotals } = this.state;
    const data = this.state[chartScope];
    const chartLoaded = data && data !== "pending";

    return (
      <AdminLayout readonly title="Device Usage Trends">
        <SectionBox noPadding>
          <div className={styles.chartContainer}>
            {!chartLoaded && (
              <div style={{ marginTop: "150px", textAlign: "center" }}>
                <CircleSpinner size={18} />
              </div>
            )}
            {chartLoaded && (
              <React.Suspense fallback={<CircleSpinner size={18} />}>
                <ChartJS
                  height="300"
                  width="800"
                  data={processData(data, "total_count")}
                  type="line"
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    legend: {
                      position: "right",
                      labels: {
                        boxWidth: 10,
                      },
                    },
                    scales: {
                      yAxes: [
                        {
                          scaleLabel: {
                            display: true,
                            labelString: "Number of Rides Uploaded",
                          },
                          ticks: {
                            beginAtZero: true,
                            callback: (v) => v.toLocaleString("en-US"),
                          },
                          stacked: true,
                        },
                      ],
                    },
                  }}
                />
              </React.Suspense>
            )}
          </div>
          <UnderlineSelector
            className={styles.headingFilter}
            styles={{ option: styles.option, selected: styles.selected }}
            options={Map({
              pastWeek: "Week",
              pastMonth: "Month",
              pastSixMonths: "Six Months",
            })}
            selected={chartScope}
            onSelectionChanged={this.setChartScope}
          />
        </SectionBox>

        <Grid cols={4}>
          <SectionBox title="30-day Breakdown (Overall)">
            {thirtyDayTotals && <HorizontalBarChart deviceCounts={thirtyDayTotals.counts} metric="total_count" />}
          </SectionBox>
          <SectionBox title="30-day Breakdown (Premium)">
            {thirtyDayTotals && <HorizontalBarChart deviceCounts={thirtyDayTotals.counts} metric="premium_count" />}
          </SectionBox>
          <SectionBox title="30-day Breakdown (Basic)">
            {thirtyDayTotals && <HorizontalBarChart deviceCounts={thirtyDayTotals.counts} metric="basic_count" />}
          </SectionBox>
          <SectionBox title="30-day Breakdown (Free)">
            {thirtyDayTotals && <HorizontalBarChart deviceCounts={thirtyDayTotals.counts} metric="free_count" />}
          </SectionBox>
        </Grid>
      </AdminLayout>
    );
  }
}

// const colors = colorbrewer.Accent[8]
// from
const colors = [
  "#868e96", // grey 6
  "#1098ad", // cyan 7
  "#f76707", // orange 7
  "#fcc419", // yellow 5
  "#dee2e6", // grey 3
  "#748ffc", // indigo 4
  "#99e9f2", // cyan 2
  "#e67700", // yellow 9
  "#ffa94d", // orange 4
];

const processData = (buckets, yAxis) => {
  const firstBucket = buckets[0];
  const devices = firstBucket.counts.map(({ device_code: deviceCode, device_name: deviceName }) => ({
    deviceCode,
    deviceName,
  }));
  const labels = buckets.map((b) => moment(b.bucket_start).format("MMM D"));
  const datasets = devices.map((device, index) => ({
    label: device.deviceName,
    data: buckets.map((b) => b.counts.find((c) => c.device_code === device.deviceCode)[yAxis]),
    backgroundColor: colors[index % colors.length],
    borderColor: "rgba(0, 0, 0, 0.1)",
    lineTension: 0,
  }));
  return { datasets, labels };
};

const OTHER_AGGREGATE_CODE = 99999;

const HorizontalBarChart = ({ deviceCounts, metric }) => {
  const noOther = deviceCounts.filter((c) => c.device_code !== OTHER_AGGREGATE_CODE);
  const other = deviceCounts.find((c) => c.device_code === OTHER_AGGREGATE_CODE);
  const sorted = _.sortBy(noOther, metric).reverse().concat([other]);
  const values = sorted.map((s) => s[metric]);
  const sum = values.reduce((a, b) => a + b) || 1;
  const max = _.max(values) || 1;

  const formatPercentage = (p) => p.toLocaleString("en-US", { maximumFractionDigits: 1 }) + "%";
  return (
    <div style={{ fontSize: "13px" }}>
      {sorted.map((count) => (
        <div key={count.device_code} style={{ position: "relative", marginBottom: "10px" }}>
          <div>
            {count.device_name} ({formatPercentage((count[metric] / sum) * 100)})
          </div>
          <div
            style={{
              backgroundColor: count.device_code === OTHER_AGGREGATE_CODE ? "#BDBDBD" : "#6982FF",
              height: "4px",
              width: formatPercentage((count[metric] / max) * 100),
            }}
          >
            &nbsp;
          </div>
        </div>
      ))}
    </div>
  );
};
